import React from 'react';
import { Chart } from 'react-charts'
import DatePicker from 'react-date-picker'

import Loader from '../Loader/Loader'
import Select from '../Select/Select'
import { Fire, Time, Tools } from '../../../services'

import ChartSelector from './ChartSelector'
import './PeriodChart.scss'

const opts = [
  { type: 'range', title: 'Période' },
  { type: 'week', title: "Cette semaine" },
  { type: 'month', title: 'Ce mois-ci' },
  { type: '3-months', title: '3 derniers mois' },
  { type: 'all-time', title: 'Depuis le début' },
]

interface Props {
  title: string;

  getNumber?: (item: any) => number;
  getDate: (item: any) => any;
  getData: (start: any, end: any) => Promise<any[]>;
}
const PeriodChart: React.FC<Props> = (props) => {

  const lastWeek = Time.moment(new Date()).subtract(7, 'days')
  const [loading, setLoading] = React.useState(false)
  const [preloaded, setPreloaded] = React.useState<any>({})
  const [data, setData] = React.useState<any[]>([])
  const [option, setOption] = React.useState<any>(opts[1])
  const [period, setPeriod] = React.useState({
    start: lastWeek.toDate(),
    end: new Date()
  })

  const chart: any[] = [{
    label: 'Series 1',
    data: data,
  }]
  
  const formatDate = (value: any) => {
    const date = new Date(value)
    date.setHours(0)
    date.setMinutes(0)
    return date
  }

  const sortBy = React.useCallback((range: any, input: any[], type: 'hours' | 'days' | 'months') => {
    const output: any[] = []
    const a = Time.moment(range.start)
    const b = Time.moment(range.end)
    const columns = b.diff(a, type)
    for (let i = 0; i < columns; ++i) {
      const d = Time.moment(a).add(i, type)
      output.push({
        x: d,
        y: 0,
      })
    }

    for (const item of input) {
      const c = Time.moment(props.getDate(item))
      const diff = c.diff(a, type)
      if (diff >= 0 && diff < output.length) {
        output[diff].y += props.getNumber ? props.getNumber(item) : 1
      }
    }
    return output
  }, [])

  const onChange = React.useCallback((key: string, value: any) => {
    period[key] = formatDate(value)
    setPeriod({ ...period })
  }, [period])

  const getSorted = React.useCallback((range: any, items: any[]) => {
    const a = Time.moment(range.start)
    const b = Time.moment(range.end)
    const days = b.diff(a, 'days')
    const months = b.diff(a, 'months')
    if (days < 2)
      return sortBy(range, items, 'hours')
    if (months < 2)
      return sortBy(range, items, 'days')
    return sortBy(range, items, 'months')
  }, [])

  const getRange = React.useCallback((type: string) => {
    const start = Time.moment(new Date())
    switch (type) {
      case "week":
        return {
          start: start.subtract(7, 'days').toDate(),
          end: new Date(),
        }
        break;

      case "month":
        return {
          start: start.subtract(1, 'months').toDate(),
          end: new Date(),
        }
        break;
      case "3-months":
        return {
          start: start.subtract(3, 'months').toDate(),
          end: new Date(),
        }
        break;
      case "all-time":
        return {
          start: start.subtract(2, 'years').toDate(),
          end: new Date(),
        }
        break;
      default:
        break;
    }
    return { start: new Date(), end: new Date() }
  }, [])

  const refreshOption = React.useCallback(async (type: string) => {
    setLoading(true)
    const range = getRange(type)
    const res = await props.getData(range.start, range.end)
    preloaded[type] = getSorted(range, res)
    setPreloaded({ ...preloaded })
    setLoading(false)
  }, [preloaded])

  const refreshPreload = React.useCallback(async () => {
    setLoading(true)
    for (const opt of opts) {
      const range = getRange(opt.type)
      const res = await props.getData(range.start, range.end)
      preloaded[opt.type] = getSorted(range, res)
    }
    setPreloaded({ ...preloaded })      
    setLoading(false)
  }, [])

  const refreshRange = React.useCallback(async () => {
    setLoading(true)
    const res = await props.getData(period.start, period.end)
    setData(getSorted(period, res))
    setLoading(false)
  }, [period])

  const refresh = React.useCallback(() => {
    if (option.type !== 'range')
      refreshOption(option.type)
    else
      refreshRange()
  }, [option])

  React.useEffect(() => {
    if (option.type === 'range')
      refreshRange()
  }, [option.type, period])


  React.useEffect(() => {
    refreshPreload()
  }, [])

  React.useEffect(() => {
    const content = preloaded[option.type]
    if (!content)
      setData([])
    else
      setData(content)
  }, [option.type, preloaded])

  const axes = [
    { primary: true, type: 'time', position: 'bottom' },
    { type: 'linear', position: 'right' },
    { type: 'linear', position: 'left' },
  ]

  const total = React.useMemo(() => {
    let count = 0
    for (const elem of data)
      count += elem.y
    return count
  }, [data])

  return (
    <div className="card card-primary">
      <div className="card-header">
        <div className="row-header">
          <h3 className="card-title">{props.title}</h3>
          <div>
            <h3 className="card-title" style={{marginRight: 10}}>{total} total</h3>
            <div onClick={refresh} style={{display: 'inline-block', cursor: 'pointer'}}><i className="fa fa-sync"></i></div>
          </div>
        </div>
      </div>
      <div className="card-body">
        <ChartSelector
          disabled={loading}
          value={option}
          onChange={setOption}
          options={opts}
          />
        { option.type === 'range' &&
          <div style={{marginBottom: 20}}>
            <DatePicker
              clearIcon={null}
              value={period.start}
              onChange={(value: any) => onChange('start', value)}
            />
            <DatePicker
              clearIcon={null}
              value={period.end}
              onChange={(value: any) => onChange('end', value)}
            />
          </div>
        }
        <div className="chart-wrapper">
          { loading || data.length === 0 ? (
            <Loader />
          ) : (
            <Chart
              options={{
                locale: 'fr-FR'
              }}
              data={chart}
              axes={axes}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default PeriodChart;
